// Counterpart: spa/proposal/src/errors/sentry/initSentry.ts
// Key differences in proposal: config comes via SDK genericState (cross-origin iframe), no
// reactRouterV5Instrumentation, and a double-init guard instead of reading window globals directly.
import * as Sentry from '@sentry/react'
import type { CaptureContext } from '@sentry/types'

export const initSentry = (isLocal: boolean) => {
  if (window.SENTRY_IO_APP_DSN && !isLocal) {
    const BLACKLISTED_FETCH_URLS = [
      'https://api.amplitude.com',
      'https://maps.googleapis.com',
    ]
    const maxMessageLength = 1200

    // TODO: remove once we can import Sentry directly into Studio
    window.captureException = Sentry.captureException

    Sentry.init({
      release: window.RELEASE_IDENTIFIER,
      dsn: window.SENTRY_IO_APP_DSN,
      environment: window.ENV,
      normalizeDepth: 5, // Controls the amount of depth that serialised objects will have. Defaults to 3.
      maxBreadcrumbs: 50,
      sampleRate: 1,
      tracesSampleRate: 0.01,
      // Session Replay: 1/100000 healthy sessions, 1/10000 sessions with errors
      replaysSessionSampleRate: 0.00001,
      replaysOnErrorSampleRate: 0.0001,
      integrations: (integrations) => {
        return [
          ...integrations,
          new Sentry.Replay({
            maskAllText: true,
            blockAllMedia: true,
            maskAllInputs: true,
          }),
          // We cannot only target window.API_ROOT beacuse it will also try to trace there results
          // after redirection too (e.g. refresh_download_url which redirects directly to a Google endpoint
          // which cannot support OPTIONS requests)
          // e.g. http://localhost:8000/api/orgs/1/maps/refresh_download_url/google/?url={base64-encoded-google-endpoint}
          //
          // Workaround is to exclude endpoints which we know may redirect to URLs not in our domain

          // Adapted from https://stackoverflow.com/a/18067509/1767169
          // Include API_ROOT but exclude certain paths after that including:
          //  - refresh_download_url

          // Their example matches entire domain except for /forms:
          // Sample Regex: https?://[^/]+($|/(?!forms)/?.*$)
          // Explanation: After the www.domain.com bit, it's looking for either the end of the string, or for a slash and then something that ISN'T forms.

          // Our implementation:
          // Regex: window.API_ROOT + '[^/]+($|/?.*(?!refresh_download_url)?.*$)'
          // Explanation:
          //    Starts with API Root
          //    Then either end of string OR
          //    Anything that does NOT contain refresh_download_url

          new Sentry.BrowserTracing({
            tracingOrigins: [window.API_ROOT + '[^/]+($|/?.*(?!refresh_download_url)?.*$)'],
            // eslint-disable-next-line no-restricted-globals
            routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
          }),
        ]
      },

      beforeBreadcrumb(breadcrumb, hint) {
        // Don't capture certain fetch/xhr requests, mainly to keep breadcrumbs relevant
        for (const url of BLACKLISTED_FETCH_URLS) {
          if (breadcrumb.data?.url?.indexOf(url) === 0) return null
        }

        // Reduce epicly long console logs
        if (
          breadcrumb.message &&
          typeof breadcrumb.message === 'string' &&
          breadcrumb.message?.length > maxMessageLength
        ) {
          breadcrumb.message = breadcrumb.message.slice(0, maxMessageLength) + '...'
          if (breadcrumb.category === 'console' && breadcrumb.data) breadcrumb.data.arguments = [] // Arguments is just a copy of the message, discard in this case
        }

        return breadcrumb
      },

      beforeSend(event, hint) {
        const error = hint?.originalException as Error | undefined

        // Customize fingerprint for HttpError to group by message instead of stacktrace
        // This prevents all HttpError instances from being grouped into a single Sentry issue.
        // Ideally in the future we instead use error codes or some other distinct identifier to group errors.
        if (error && error.name === 'HttpError') {
          event.fingerprint = ['HttpError', error.message || 'unknown']
        }

        return event
      },
    })
  } else {
    console.warn('Sentry not initialized, no SENTRY_IO_APP_DSN')
  }
}
declare global {
  interface Window {
    RELEASE_IDENTIFIER: string
    SENTRY_IO_APP_DSN?: string
    captureException: (error: any, options?: CaptureContext) => void
  }
}
